/**
 * The Join links page's Period (join-links-cms T3, C-DAY). Days are UAE days (fixed UTC+4,
 * the same rule as the server) written `yyyy-mm-dd`. This only PICKS the range sent to the
 * API — the server buckets the data; the browser never re-buckets or parses a day into a
 * local `Date` (which would shift days west of UTC).
 */
export type JoinLinkPreset = "7d" | "30d" | "all";

export interface JoinLinkRange {
  /** First UAE day, inclusive (`yyyy-mm-dd`). */
  from: string;
  /** Last UAE day, inclusive (`yyyy-mm-dd`). */
  to: string;
}

/** Before phase 1 shipped, so no open or non-estimate join can predate it ("All time"). */
export const JOIN_LINKS_EPOCH = "2026-10-01";

export const DEFAULT_JOIN_LINK_PRESET: JoinLinkPreset = "30d";

const UAE_OFFSET_MS = 4 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Today's UAE day: the UTC instant + 4 h, as `yyyy-mm-dd`. */
export function uaeToday(now: Date = new Date()): string {
  return new Date(now.getTime() + UAE_OFFSET_MS).toISOString().slice(0, 10);
}

/** `day` ± `days`, on the calendar only (splits the string; UTC arithmetic, no local time). */
export function addUaeDays(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d) + days * DAY_MS).toISOString().slice(0, 10);
}

/** The inclusive UAE-day range of a preset, ending today. */
export function joinLinkRange(preset: JoinLinkPreset, now: Date = new Date()): JoinLinkRange {
  const to = uaeToday(now);
  if (preset === "7d") return { from: addUaeDays(to, -6), to };
  if (preset === "30d") return { from: addUaeDays(to, -29), to };
  // String order is day order for yyyy-mm-dd; never send from > to (the API answers 400).
  return { from: JOIN_LINKS_EPOCH < to ? JOIN_LINKS_EPOCH : to, to };
}
